import { EditorBlock } from '~/entities/blog-posts/model/editor'
import { ImageModel } from '~/shared/api/services/global/types'

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
export type ImagePosition = 'left' | 'right'

export interface TextBlockValue {
  text: string
  link: string
}

export interface HeadingBlockValue extends TextBlockValue {
  level: HeadingLevel
}

export interface ImageBlockValue {
  image: ImageModel | null
}

export interface MediaTextBlockValue {
  image: ImageModel | null
  text: string
  imagePosition: ImagePosition
  link: string
}

export interface ProductBlockValue {
  mode: 'products' | 'categories'
  ids: string[]
  titles: Record<string, string>
}

export interface PostLinkBlockValue {
  text: string
}

const PRODUCT_SHORTCODE_TAG_PATTERN = 'products?'
const PRODUCT_CATEGORY_SHORTCODE_TAG_PATTERN = 'product_category|products?'
const PRODUCT_SHORTCODE_TAG = 'product'
const PRODUCT_CATEGORY_SHORTCODE_TAG = 'product_category'
const LEGACY_PRODUCT_SHORTCODE_TAG = 'products'

const escapeShortcodeAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')

const decodeHtmlEntities = (value: string) =>
  value.replace(/&quot;/g, '"').replace(/&amp;/g, '&')

export const splitPostLinkEntries = (value: string) =>
  value
    .split(/<br\s*\/?>/gi)
    .map((entry) => entry.trim())
    .filter(Boolean)

export const hasLinkedPostEntries = (value: string) =>
  splitPostLinkEntries(value).every((entry) =>
    /<a\b[^>]*href\s*=\s*['"][^'"]+['"][^>]*>/i.test(entry),
  )

export const parseProductsShortcodeIds = (value: string) => {
  return Array.from(
    value.matchAll(
      new RegExp(
        `\\[${PRODUCT_SHORTCODE_TAG_PATTERN}\\s+id="([^"]*)"[^\\]]*\\]`,
        'gi',
      ),
    ),
  )
    .map((match) => match[1]?.trim() || '')
    .filter(Boolean)
}

export const parseProductShortcodeTitles = (
  value: string,
  field: 'id' | 'category',
) => {
  const tagPattern =
    field === 'category'
      ? PRODUCT_CATEGORY_SHORTCODE_TAG_PATTERN
      : PRODUCT_SHORTCODE_TAG_PATTERN
  const fieldPattern = field === 'category' ? 'slug' : 'id'

  const matches = Array.from(
    value.matchAll(
      new RegExp(
        `\\[${tagPattern}\\s+(?:${fieldPattern})="([^"]*)"(?:\\s+title="([^"]*)")?\\]`,
        'gi',
      ),
    ),
  )

  return matches.reduce<Record<string, string>>((acc, match) => {
    const id = match[1]?.trim() || ''
    const title = decodeHtmlEntities(match[2]?.trim() || '')

    if (id && title) {
      acc[id] = title
    }

    return acc
  }, {})
}

export const parseProductsShortcodeCategoryIds = (value: string) => {
  const matches = Array.from(
    value.matchAll(
      new RegExp(
        `\\[${PRODUCT_CATEGORY_SHORTCODE_TAG_PATTERN}\\s+slug="([^"]*)"[^\\]]*\\]`,
        'gi',
      ),
    ),
  )

  if (!matches.length) {
    return []
  }

  return matches
    .flatMap((match) => (match[1] || '').split(','))
    .map((id) => id.trim())
    .filter(Boolean)
}

export const formatProductsShortcode = ({
  mode,
  ids,
  titles = {},
  variant = 'render',
}: {
  mode: 'products' | 'categories'
  ids: string[]
  titles?: Record<string, string>
  variant?: 'editor' | 'render'
}) => {
  const shortcodeField = mode === 'categories' ? 'slug' : 'id'
  let shortcodeTag = LEGACY_PRODUCT_SHORTCODE_TAG

  if (variant === 'editor') {
    shortcodeTag =
      mode === 'categories'
        ? PRODUCT_CATEGORY_SHORTCODE_TAG
        : PRODUCT_SHORTCODE_TAG
  }

  if (!ids.length) {
    return `[${shortcodeTag} ${shortcodeField}=""]`
  }

  return ids
    .map((id) => {
      const title = titles[id]?.trim()
      const titleAttribute = title
        ? ` title="${
            variant === 'editor' ? title : escapeShortcodeAttribute(title)
          }"`
        : ''

      return `[${shortcodeTag} ${shortcodeField}="${id}"${titleAttribute}]`
    })
    .join('\n')
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const parseJsonRecord = (content: string) => {
  if (!content) {
    return null
  }

  try {
    const parsed = JSON.parse(content) as unknown

    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

const parseImageModel = (value: unknown): ImageModel | null => {
  if (!isRecord(value) || typeof value.url !== 'string' || !value.url) {
    return null
  }

  return value as ImageModel
}

export const isVideoMedia = (value: ImageModel | null | undefined) => {
  if (!value) {
    return false
  }

  if (value.uploadType === 'video') {
    return true
  }

  return typeof value.type === 'string' && value.type.startsWith('video/')
}

const parseLink = (value: unknown) =>
  typeof value === 'string' ? value.trim() : ''

export const parseTextBlockContent = (content: string): TextBlockValue => {
  const parsed = parseJsonRecord(content)

  if (!parsed) {
    return {
      text: content || '',
      link: '',
    }
  }

  return {
    text: typeof parsed.text === 'string' ? parsed.text : content || '',
    link: parseLink(parsed.link),
  }
}

export const stringifyTextBlockContent = (value: TextBlockValue) =>
  JSON.stringify({
    text: value.text,
    link: value.link.trim(),
  })

export const parseHeadingBlockContent = (
  content: string,
): HeadingBlockValue => {
  const parsed = parseJsonRecord(content)

  if (!parsed) {
    return {
      text: content || '',
      level: 'h2',
      link: '',
    }
  }

  const { level } = parsed

  return {
    text: typeof parsed.text === 'string' ? parsed.text : content || '',
    level:
      typeof level === 'string' &&
      ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(level)
        ? (level as HeadingLevel)
        : 'h2',
    link: parseLink(parsed.link),
  }
}

export const stringifyHeadingBlockContent = (value: HeadingBlockValue) =>
  JSON.stringify({
    text: value.text,
    level: value.level,
    link: value.link.trim(),
  })

export const parseImageBlockContent = (content: string): ImageBlockValue => {
  const parsed = parseJsonRecord(content)

  if (!parsed) {
    return {
      image: null,
    }
  }

  const image = parseImageModel(parsed.image) || parseImageModel(parsed)

  return {
    image,
  }
}

export const stringifyImageBlockContent = (value: ImageBlockValue) =>
  JSON.stringify({
    image: value.image,
  })

export const parseMediaTextBlockContent = (
  content: string,
): MediaTextBlockValue => {
  const parsed = parseJsonRecord(content)

  if (!parsed) {
    return {
      image: null,
      text: content || '',
      imagePosition: 'left',
      link: '',
    }
  }

  return {
    image: parseImageModel(parsed.image),
    text: typeof parsed.text === 'string' ? parsed.text : '',
    imagePosition: parsed.imagePosition === 'right' ? 'right' : 'left',
    link: parseLink(parsed.link),
  }
}

export const stringifyMediaTextBlockContent = (value: MediaTextBlockValue) =>
  JSON.stringify({
    image: value.image,
    text: value.text,
    imagePosition: value.imagePosition,
    link: value.link.trim(),
  })

export const parseProductBlockContent = (
  content: string,
): ProductBlockValue => {
  const parsed = parseJsonRecord(content)

  if (!parsed) {
    const productIds = parseProductsShortcodeIds(content)
    const categoryIds = parseProductsShortcodeCategoryIds(content)
    const productTitles = parseProductShortcodeTitles(content, 'id')
    const categoryTitles = parseProductShortcodeTitles(content, 'category')

    return {
      mode: categoryIds.length ? 'categories' : 'products',
      ids: categoryIds.length ? categoryIds : productIds,
      titles: categoryIds.length ? categoryTitles : productTitles,
    }
  }

  return {
    mode: parsed.mode === 'categories' ? 'categories' : 'products',
    ids: Array.isArray(parsed.ids)
      ? parsed.ids.filter((id): id is string => typeof id === 'string')
      : [],
    titles: isRecord(parsed.titles)
      ? Object.entries(parsed.titles).reduce<Record<string, string>>(
          (acc, [id, title]) => {
            if (typeof title === 'string' && title.trim()) {
              acc[id] = title
            }

            return acc
          },
          {},
        )
      : {},
  }
}

export const stringifyProductBlockContent = (value: ProductBlockValue) =>
  JSON.stringify({
    mode: value.mode,
    ids: value.ids,
    titles: value.titles,
  })

export const parsePostLinkBlockContent = (
  content: string,
): PostLinkBlockValue => {
  const parsed = parseJsonRecord(content)

  if (!parsed) {
    return {
      text: content || '',
    }
  }

  if (typeof parsed.text === 'string') {
    return {
      text: parsed.text,
    }
  }

  const slug = typeof parsed.slug === 'string' ? parsed.slug : ''
  const title = typeof parsed.title === 'string' ? parsed.title : ''

  return {
    text: slug && title ? title : '',
  }
}

export const hasValidPostLinkContent = (content: string) => {
  const postLinkValue = parsePostLinkBlockContent(content)
  const text = postLinkValue.text.trim()

  if (!text) {
    return true
  }

  return hasLinkedPostEntries(text)
}

export const stringifyPostLinkBlockContent = (value: PostLinkBlockValue) =>
  JSON.stringify({
    text: value.text,
  })

export const stripHtmlTags = (value: string) =>
  value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(div|p|li|blockquote|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{2,}/g, '\n')
    .trim()

export const getBlockTextContent = (
  block: Pick<EditorBlock, 'kind' | 'content'>,
) => {
  switch (block.kind) {
    case 'heading':
      return stripHtmlTags(parseHeadingBlockContent(block.content).text)
    case 'mediaText':
      return stripHtmlTags(parseMediaTextBlockContent(block.content).text)
    case 'product':
    case 'productCategory':
      return stripHtmlTags(
        parseProductBlockContent(block.content).ids.join(', '),
      )
    case 'postLink':
      return stripHtmlTags(parsePostLinkBlockContent(block.content).text)
    case 'image':
    case 'cover':
      return ''
    default:
      return stripHtmlTags(parseTextBlockContent(block.content).text)
  }
}
